import { BlockStack } from '@shopify/polaris';
import type { Colour } from '../types';
import {
  GAUGE_SIZES,
  getScoreTierConfig,
  SEMANTIC_COLORS,
  BRAND_COLORS,
  type GaugeSize,
} from '../theme';
import { useZeliumTheme } from '../theme';

/**
 * T040: ScoreGauge component.
 * Circular score visualization with animated ring fill, tier label, and colour.
 * Uses theme tokens for colours and motion tokens for animation.
 *
 * @see documents/ui-enhancement-spec.md §10.3, §13
 */
interface ScoreGaugeProps {
  score: number;
  colour: Colour;
  size?: GaugeSize;
  /** Show accent glow behind the ring for scores ≥ 70 */
  showGlow?: boolean;
}

export function ScoreGauge({ score, colour, size = 'medium', showGlow }: ScoreGaugeProps) {
  const { motion } = useZeliumTheme();
  const dimensions = GAUGE_SIZES[size];
  const { diameter, strokeWidth, fontSize, gradeSize } = dimensions;

  const radius = (diameter - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = (score / 100) * circumference;
  const offset = circumference - progress;

  const tierConfig = getScoreTierConfig(score);
  const strokeColor = getStrokeColor(colour, tierConfig.hex);
  const shouldGlow = showGlow ?? tierConfig.hasGlow;

  return (
    <BlockStack gap="100" inlineAlign="center">
      <div
        style={{
          filter: shouldGlow ? `drop-shadow(${`0 0 12px ${tierConfig.hex}40`})` : undefined,
          lineHeight: 0,
        }}
      >
        <svg
          width={diameter}
          height={diameter}
          viewBox={`0 0 ${diameter} ${diameter}`}
          style={{ transform: 'rotate(-90deg)' }}
          role="img"
          aria-label={`Score: ${score} out of 100, ${tierConfig.label}`}
        >
          {/* Background circle */}
          <circle
            cx={diameter / 2}
            cy={diameter / 2}
            r={radius}
            fill="none"
            stroke="var(--p-color-bg-fill-disabled, #e4e5e7)"
            strokeWidth={strokeWidth}
          />
          {/* Progress arc */}
          <circle
            cx={diameter / 2}
            cy={diameter / 2}
            r={radius}
            fill="none"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            strokeLinecap="round"
            style={{
              transition: `stroke-dashoffset ${motion.duration.dramatic}s ${motion.ease.default}`,
            }}
          />
          {/* Score text */}
          <text
            x={diameter / 2}
            y={diameter / 2 - 4}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={fontSize}
            fontWeight="bold"
            fill={`var(--z-primary, ${BRAND_COLORS.primaryDark})`}
            style={{ transform: 'rotate(90deg)', transformOrigin: 'center' }}
          >
            {score}
          </text>
          {/* Tier label */}
          <text
            x={diameter / 2}
            y={diameter / 2 + fontSize / 2 + 4}
            textAnchor="middle"
            dominantBaseline="central"
            fontSize={gradeSize}
            fill="var(--p-color-text-subdued, #6b7177)"
            style={{ transform: 'rotate(90deg)', transformOrigin: 'center' }}
          >
            {tierConfig.label}
          </text>
        </svg>
      </div>
    </BlockStack>
  );
}

/**
 * Map legacy Colour type to theme-aware stroke colour.
 * Falls back to tier-derived hex from theme config.
 */
function getStrokeColor(colour: Colour, tierHex: string): string {
  switch (colour) {
    case 'green':
      return `var(--z-score-good, ${SEMANTIC_COLORS.good})`;
    case 'yellow':
      return `var(--z-score-warning, ${SEMANTIC_COLORS.warning})`;
    case 'orange':
      return `var(--z-score-warning, ${SEMANTIC_COLORS.warning})`;
    case 'red':
      return `var(--z-score-critical, ${SEMANTIC_COLORS.critical})`;
    default:
      return tierHex;
  }
}
